<template>
  <div v-if="!isPersonalProject">
    <!-- Link Telegram Group (when no group is linked) -->
    <div v-if="!project?.telegramChatId" class="form-row">
      <label>{{ s__('ProjectSettings|Link Telegram Group') }}</label>
      <div
        class="help-text"
        style="
          margin-bottom: 12px;
          padding: 12px;
          background-color: #f0f7ff;
          border: 1px solid #d0e7ff;
          border-radius: 8px;
        "
      >
        {{
          s__(
            'ProjectSettings|Linking a Telegram group allows sending notifications about task events and regular reports to the group chat (in addition to direct messages).'
          )
        }}
      </div>
      <div style="display: flex; gap: 8px; align-items: flex-start">
        <input
          v-model="telegramChatIdInput"
          type="text"
          :placeholder="s__('ProjectSettings|Enter Telegram group chat ID')"
          :disabled="linkingGroup"
          style="flex: 1"
        />
        <button
          class="btn-primary btn-sm"
          :disabled="!telegramChatIdInput || linkingGroup"
          @click="linkTelegramGroup"
        >
          <span v-if="linkingGroup">{{ s__('ProjectSettings|Linking...') }}</span>
          <span v-else>{{ s__('ProjectSettings|Link Group') }}</span>
        </button>
      </div>
      <div v-if="linkGroupError" class="error" style="margin-top: 8px">{{ linkGroupError }}</div>
      <div class="help-text" style="margin-top: 8px; margin-bottom: 8px">
        {{
          s__(
            'ProjectSettings|Make sure the bot is added to the group before linking. Enter the group chat ID (usually a negative number).'
          )
        }}
      </div>
      <div class="telegram-group-instructions">
        <div style="font-weight: 600; margin-bottom: 8px">
          {{ s__('ProjectSettings|How to get the group chat ID:') }}
        </div>
        <ol style="margin: 0; padding-left: 20px">
          <li style="margin-bottom: 4px">
            {{ s__('ProjectSettings|Invite a bot like @myidbot to the group chat.') }}
          </li>
          <li style="margin-bottom: 4px">
            {{ s__('ProjectSettings|Send a /getgroupid@myidbot command to the group chat.') }}
          </li>
          <li>{{ s__('ProjectSettings|The bot will respond with the group ID.') }}</li>
        </ol>
      </div>
    </div>

    <!-- Telegram Group Status (when group is linked) -->
    <div
      v-if="project?.telegramChatId"
      class="form-row"
      style="
        margin-top: 24px;
        margin-bottom: 24px;
        padding: 16px;
        background-color: #f0f7ff;
        border: 1px solid #d0e7ff;
        border-radius: 8px;
      "
    >
      <div
        style="display: flex; justify-content: space-between; align-items: flex-start; gap: 12px"
      >
        <div style="flex: 1">
          <label>{{ s__('ProjectSettings|Telegram Group') }}</label>
          <div class="muted">
            {{
              sprintf(s__('ProjectSettings|Linked to chat ID: %{chat_id}'), {
                chat_id: project.telegramChatId,
              })
            }}
          </div>
          <div class="help-text">
            {{ s__('ProjectSettings|This project is linked to a Telegram group') }}
          </div>
        </div>
        <button
          class="unlink-btn"
          :disabled="unlinkingGroup"
          :title="s__('ProjectSettings|Unlink Telegram group')"
          @click="unlinkTelegramGroup"
        >
          <span v-if="unlinkingGroup">...</span>
          <span v-else>🔗</span>
        </button>
      </div>
      <div v-if="unlinkGroupError" class="error" style="margin-top: 8px">
        {{ unlinkGroupError }}
      </div>
    </div>

    <!-- Enable Group Chat Notifications (only when group is linked) -->
    <div v-if="project?.telegramChatId" class="form-row">
      <label style="display: flex; align-items: center; gap: 8px">
        <input v-model="groupChatNotificationsEnabled" type="checkbox" :disabled="saving" />
        <span>{{ s__('ProjectSettings|Enable Group Chat Notifications') }}</span>
      </label>
      <div class="help-text">
        {{
          s__('ProjectSettings|Send notifications to the project group chat when task events occur')
        }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, sprintf } from '@/locale'

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  projectId: {
    type: String,
    required: true,
  },
  isPersonalProject: {
    type: Boolean,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  groupChatNotificationsEnabled: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['update:groupChatNotificationsEnabled', 'project-updated'])

const groupChatNotificationsEnabled = computed({
  get: () => props.groupChatNotificationsEnabled,
  set: value => emit('update:groupChatNotificationsEnabled', value),
})

const telegramChatIdInput = ref('')
const linkingGroup = ref(false)
const linkGroupError = ref('')
const unlinkingGroup = ref(false)
const unlinkGroupError = ref('')

const LINK_TELEGRAM_GROUP = gql`
  mutation LinkTelegramGroup($projectId: ID!, $telegramChatId: BigInt!) {
    linkTelegramGroup(projectId: $projectId, telegramChatId: $telegramChatId) {
      project {
        id
        telegramChatId
      }
      errors
    }
  }
`

const UNLINK_TELEGRAM_GROUP = gql`
  mutation UnlinkTelegramGroup($projectId: ID!) {
    unlinkTelegramGroup(projectId: $projectId) {
      project {
        id
        telegramChatId
      }
      errors
    }
  }
`

const { mutate: linkTelegramGroupMutation } = useMutation(LINK_TELEGRAM_GROUP)
const { mutate: unlinkTelegramGroupMutation } = useMutation(UNLINK_TELEGRAM_GROUP)

async function linkTelegramGroup() {
  if (!telegramChatIdInput.value) return

  linkingGroup.value = true
  linkGroupError.value = ''

  try {
    // Validate that the input is a valid number (can be negative)
    const chatIdStr = telegramChatIdInput.value.trim()
    if (!/^-?\d+$/.test(chatIdStr)) {
      linkGroupError.value = s__('ProjectSettings|Invalid chat ID. Please enter a valid number.')
      linkingGroup.value = false
      return
    }

    // Pass as string - GraphQL BigInt accepts string representation
    const { data } = await linkTelegramGroupMutation({
      projectId: props.projectId,
      telegramChatId: chatIdStr,
    })

    if (data?.linkTelegramGroup?.errors?.length) {
      linkGroupError.value = data.linkTelegramGroup.errors.join(', ')
    } else {
      telegramChatIdInput.value = ''
      linkGroupError.value = ''
      emit('project-updated')
    }
  } catch (e) {
    linkGroupError.value =
      s__('ProjectSettings|Failed to link Telegram group') +
      ': ' +
      (e.message || s__('ProjectSettings|Unknown error'))
  } finally {
    linkingGroup.value = false
  }
}

async function unlinkTelegramGroup() {
  if (!props.project?.telegramChatId) return

  // Confirm before unlinking
  const confirmed = confirm(
    s__(
      'ProjectSettings|Are you sure you want to unlink this Telegram group? This will disable group chat notifications and regular reports.'
    )
  )
  if (!confirmed) return

  unlinkingGroup.value = true
  unlinkGroupError.value = ''

  try {
    const { data } = await unlinkTelegramGroupMutation({
      projectId: props.projectId,
    })

    if (data?.unlinkTelegramGroup?.errors?.length) {
      unlinkGroupError.value = data.unlinkTelegramGroup.errors.join(', ')
    } else {
      unlinkGroupError.value = ''
      emit('project-updated')
    }
  } catch (e) {
    unlinkGroupError.value =
      s__('ProjectSettings|Failed to unlink Telegram group') +
      ': ' +
      (e.message || s__('ProjectSettings|Unknown error'))
  } finally {
    unlinkingGroup.value = false
  }
}
</script>

<style scoped>
.telegram-group-instructions {
  padding: 16px;
  background-color: #f0f7ff;
  border: 1px solid #d0e7ff;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
}

.unlink-btn {
  padding: 4px 8px;
  font-size: 12px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #999;
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.2s;
}

.unlink-btn:hover {
  opacity: 1;
  color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
}

.unlink-btn:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.form-row select,
.form-row input[type='text'] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
}

.help-text {
  margin-top: 4px;
  font-size: 13px;
  color: #666;
}
</style>
